import { useMutation, useQueryClient } from '@tanstack/react-query';
import { showSnackbar } from '../../../api/snackbarSlice';
import { useDispatch } from 'react-redux';
import { apiClient } from '../../../utils/axios';
import { isEmpty } from 'lodash';
import { useState } from 'react';
import { AxiosError } from 'axios';
import { getDateInUtcFormatted, getTimeInUtcFormatted } from '../../../utils/formatDate';
import dayjs from 'dayjs';
import { getAtomicFields } from '../../../helper/CreateDocumentsHelper';

const create = async (payload) => {
  const response = await apiClient.post('/invoices', payload);
  return response.data;
};

const edit = async (payload) => {
  const { data } = apiClient.put('/invoices/' + payload.document_id, payload);
  return data;
};

const createInvoice = async ({ payload, existingStatus }) => {
  // Scenario 1: New Document saved as draft, Use post
  // Scenario 2: Existing Document (draft) converted to e-Document, Use put
  // Scenario 3: Existing Document (draft) edited, Use put
  // Scenario 4: Existing Document (e-Document) edited and resubmitted, Use post
  let exisitngDocument = !isEmpty(payload.document_id);
  if (exisitngDocument) {
    // Existing draft being converted to e-Document
    if (existingStatus === 'draft' && payload.status === 'active') {
      return edit(payload);
    }

    // Existing e-doc being edited and resubmitted
    if (existingStatus === 'active' && payload.status === 'active') {
      return create(payload);
    }

    // Existing draft being edited
    return edit(payload);
  } else {
    // New document being created
    return create(payload);
  }
};

export const useCreateInvoiceHandler = ({ onSuccess }) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const [error, setError] = useState(null);

  const mutation = useMutation({
    mutationFn: createInvoice,
    onSuccess: (data, variables, context) => {
      dispatch(showSnackbar({ message: 'Invoice created successfully.' }));
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      onSuccess && onSuccess(variables);
    },
    onError: (error) => {
      if (error instanceof AxiosError) {
        if (error.response.status === 400) {
          setError(error.response.data.errors);
          return;
        }
        dispatch(showSnackbar({ message: error.response.data, severity: 'error' }));
      }
      dispatch(showSnackbar({ message: error, severity: 'error' }));
    },
  });

  const onSubmit = async (data, existingStatus) => {
    const docDateInMysTz = dayjs(data.document_date).tz('Asia/Kuala_Lumpur').format('YYYY-MM-DD');
    const payload = {
      ...getAtomicFields(data),
      document_date: getDateInUtcFormatted(docDateInMysTz, data.document_time),
      document_time: getTimeInUtcFormatted(docDateInMysTz, data.document_time),
      payment_mode: data?.payment_mode?.value,
      billed_to: data?.billed_to || {},
    };

    if (payload.conversion_rate_per_myr === null) {
      payload.conversion_rate_per_myr = 1;
    }
    payload.item_details.forEach((item) => {
      delete item._id;
    });

    mutation.mutate({ payload, existingStatus });
  };

  return {
    data: mutation.data,
    isError: mutation.isError,
    error: error,
    isPending: mutation.isPending,
    isSuccess: mutation.isSuccess,
    onSubmit: onSubmit,
  };
};
